import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import {
  Avatar,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  makeStyles
} from "@material-ui/core";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import TicketAgendaModal from "../TicketAgendaModal";
import TicketOrdersModal from "../TicketOrdersModal";
import TicketProcessesModal from "../TicketProcessesModal";
import TicketRemindersModal from "../TicketRemindersModal";
import TicketStreamingModal from "../TicketStreamingModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import { hasMenuPermission } from "../../layout/MainListItems";
import { ticketToolsUpdatedEvent } from "../../utils/ticketEvents";
import openSocket from "../../services/socket-io";

const useStyles = makeStyles(theme => ({
  container: {
    position: "absolute",
    left: 12,
    bottom: 126,
    zIndex: 20,
    display: "grid",
    gap: 10,
    [theme.breakpoints.down("sm")]: {
      bottom: 120,
    },
  },
  button: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    color: "#fff",
    border: "none",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
    transition: "transform 0.2s, background-color 0.2s",
    "&:hover": {
      transform: "scale(1.06)",
    },
  },
  active: {
    backgroundColor: "#148040",
  },
  inactive: {
    backgroundColor: "#6c757d",
  },
  ignored: {
    backgroundColor: "#f8d7da",
    color: "#b4232f",
    fontSize: 0,
    "&::before": {
      content: '"🚫"',
      fontSize: 18,
    },
  },
  ignoredContactRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1.5),
    border: "1px solid #e0e0e0",
    borderRadius: 6,
    marginBottom: theme.spacing(2),
  },
  ignoredContactInfo: {
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  ignoredContactText: {
    minWidth: 0,
  },
  ignoredContactName: {
    fontWeight: 600,
  },
  ignoredList: {
    marginTop: theme.spacing(1),
  },
  ignoredListItem: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  ignoredListAvatar: {
    marginRight: theme.spacing(1.5),
  },
}));

const hasActiveStreamingAssignment = (accounts, contactId) => {
  if (!contactId) return false;
  const activeStatuses = ["active", "assigned"];

  return (accounts || []).some(account => {
    const hasAccountAssignment = (account.assignments || []).some(assignment => {
      const assignmentContactId = assignment.contactId || assignment.contact?.id;
      return activeStatuses.includes(assignment.status) && String(assignmentContactId) === String(contactId);
    });

    if (hasAccountAssignment) return true;

    return (account.profiles || []).some(profile =>
      (profile.assignments || []).some(assignment => {
        const assignmentContactId = assignment.contactId || assignment.contact?.id;
        return activeStatuses.includes(assignment.status) && String(assignmentContactId) === String(contactId);
      })
    );
  });
};

const TicketFloatingControls = ({ ticket, toolsOpen, onToggleTools }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [aiActive, setAiActive] = useState(false);
  const [chatbotActive, setChatbotActive] = useState(false);
  const [aiMenuAnchor, setAiMenuAnchor] = useState(null);
  const [chatbotMenuAnchor, setChatbotMenuAnchor] = useState(null);
  const [ticketRemindersModalOpen, setTicketRemindersModalOpen] = useState(false);
  const [ticketAgendaModalOpen, setTicketAgendaModalOpen] = useState(false);
  const [ticketOrdersModalOpen, setTicketOrdersModalOpen] = useState(false);
  const [ticketProcessesModalOpen, setTicketProcessesModalOpen] = useState(false);
  const [ticketStreamingModalOpen, setTicketStreamingModalOpen] = useState(false);
  const [ignoredContactsModalOpen, setIgnoredContactsModalOpen] = useState(false);
  const [ignoredContacts, setIgnoredContacts] = useState([]);
  const [loadingIgnoredContacts, setLoadingIgnoredContacts] = useState(false);
  const [savingIgnoredContact, setSavingIgnoredContact] = useState(false);
  const [chatbotIgnored, setChatbotIgnored] = useState(false);
  const [aiIgnored, setAiIgnored] = useState(false);
  const [ignoredContactsTool, setIgnoredContactsTool] = useState("chatbot");
  const [pendingIgnoredAction, setPendingIgnoredAction] = useState(null);
  const [toolStates, setToolStates] = useState({
    reminders: false,
    agenda: false,
    orders: false,
    processes: false,
    streaming: false,
  });
  const pendingActionRef = useRef("");
  const visibleTools = useMemo(
    () => ({
      chatbot: hasMenuPermission(user, "chatbot"),
      ai: hasMenuPermission(user, "ai"),
      reminders: hasMenuPermission(user, "reminders"),
      agenda: hasMenuPermission(user, "agenda"),
      orders: hasMenuPermission(user, "orders"),
      processes: hasMenuPermission(user, "processes"),
      streaming: hasMenuPermission(user, "streaming"),
    }),
    [user]
  );

  const fetchStatus = useCallback(async () => {
    if (!ticket?.id) return;

    try {
      const contactId = ticket.contactId || ticket.contact?.id;
      const start = new Date();
      start.setFullYear(start.getFullYear() - 1);
      const end = new Date();
      end.setFullYear(end.getFullYear() + 1);
      const [
        { data: aiData },
        { data: chatbotData },
        remindersResult,
        agendaResult,
        ordersResult,
        processesResult,
        streamingResult,
      ] = await Promise.all([
        visibleTools.ai
          ? api.get(`/tickets/${ticket.id}/ai`)
          : Promise.resolve({ data: { isActive: false } }),
        visibleTools.chatbot
          ? api.get(`/tickets/${ticket.id}/chatbot`)
          : Promise.resolve({ data: { isActive: false } }),
        visibleTools.reminders
          ? api.get(`/tickets/${ticket.id}/reminders`)
          : Promise.resolve({ data: { reminders: [] } }),
        visibleTools.agenda
          ? api.get("/appointments/reservations", {
              params: {
                start: start.toISOString(),
                end: end.toISOString(),
                contactId,
                connectionId: ticket.connectionId,
              },
            })
          : Promise.resolve({ data: { reservations: [] } }),
        visibleTools.orders
          ? api.get("/orders", { params: { ticketId: ticket.id } })
          : Promise.resolve({ data: { orders: [] } }),
        visibleTools.processes
          ? api.get("/ai-processes/runs", { params: { ticketId: ticket.id } })
          : Promise.resolve({ data: { runs: [] } }),
        visibleTools.streaming
          ? api.get("/streaming/accounts")
          : Promise.resolve({ data: { accounts: [] } }),
      ]);
      setAiActive(Boolean(aiData.isActive));
      setAiIgnored(Boolean(aiData.isIgnored));
      setChatbotActive(chatbotData.isActive !== false);
      setChatbotIgnored(Boolean(chatbotData.isIgnored));
      setToolStates({
        reminders: Boolean(remindersResult.data?.reminders?.length),
        agenda: Boolean(agendaResult.data?.reservations?.length),
        orders: Boolean(ordersResult.data?.orders?.length),
        processes: Boolean(processesResult.data?.runs?.length),
        streaming: hasActiveStreamingAssignment(streamingResult.data?.accounts, contactId),
      });
    } catch (err) {
      toastError(err);
    }
  }, [ticket?.connectionId, ticket?.contact?.id, ticket?.contactId, ticket?.id, visibleTools]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    const handleTicketToolsUpdated = event => {
      if (String(event.detail?.ticketId || "") !== String(ticket?.id || "")) {
        return;
      }

      if (event.detail?.tool === "ai") {
        setAiActive(Boolean(event.detail.isActive));
      }

      if (event.detail?.tool === "chatbot") {
        setChatbotActive(event.detail.isActive !== false);
      }

      fetchStatus();
    };

    window.addEventListener(ticketToolsUpdatedEvent, handleTicketToolsUpdated);

    return () => {
      window.removeEventListener(ticketToolsUpdatedEvent, handleTicketToolsUpdated);
    };
  }, [fetchStatus, ticket?.id]);

  useEffect(() => {
    if (!ticket?.id || !visibleTools.processes) return undefined;

    const socket = openSocket();

    socket.on("aiProcesses", data => {
      const runTicketId = data?.run?.ticketId || data?.ticketId;
      if (String(runTicketId || "") !== String(ticket.id)) return;

      if (["create", "update"].includes(data.action)) {
        setToolStates(prev => ({ ...prev, processes: true }));
      }

      fetchStatus();
    });

    return () => socket.disconnect();
  }, [fetchStatus, ticket?.id, visibleTools.processes]);

  const runAiAction = async action => {
    if (pendingActionRef.current) return;

    pendingActionRef.current = "ai";

    try {
      const { data } = await api.put(`/tickets/${ticket.id}/ai`, { action });
      setAiActive(Boolean(data.isActive));
      setAiIgnored(Boolean(data.isIgnored));
      const successMessages = {
        activate: "IA activada en este chat.",
        activateReset: "IA activada y reseteada en este chat.",
        deactivate: "IA apagada en este chat.",
        deactivateReset: "IA apagada y reseteada en este chat.",
      };
      toast.success(successMessages[action]);
    } catch (err) {
      toastError(err);
      fetchStatus();
    } finally {
      pendingActionRef.current = "";
    }
  };

  const runChatbotAction = async action => {
    if (pendingActionRef.current) return;

    pendingActionRef.current = "chatbot";

    try {
      const { data } = await api.put(`/tickets/${ticket.id}/chatbot`, { action });
      setChatbotActive(data.isActive !== false);
      const successMessages = {
        activate: "Chatbot activado en este chat.",
        activateReset: "Chatbot activado y reseteado en este chat.",
        deactivate: "Chatbot apagado en este chat.",
        deactivateReset: "Chatbot apagado y reseteado en este chat.",
      };
      toast.success(successMessages[action]);
    } catch (err) {
      toastError(err);
      fetchStatus();
    } finally {
      pendingActionRef.current = "";
    }
  };

  const currentContact = ticket?.contact || {};
  const currentContactId = ticket?.contactId || currentContact.id;
  const currentContactName = currentContact.name || currentContact.number || "Contacto actual";
  const currentContactNumber = currentContact.number || currentContact.chatId || "";
  const currentContactAvatar = currentContact.profilePicUrl || "";
  const currentContactIgnored = ignoredContacts.some(
    item => String(item.contactId || item.contact?.id || "") === String(currentContactId || "")
  );
  const ignoredContactsEndpoint =
    ignoredContactsTool === "ai" ? "/ai/ignored-contacts" : "/chatbot/ignored-contacts";
  const ignoredContactsToolLabel = ignoredContactsTool === "ai" ? "IA" : "Chatbot";

  const fetchIgnoredContacts = useCallback(async () => {
    setLoadingIgnoredContacts(true);

    try {
      const { data } = await api.get(ignoredContactsEndpoint);
      setIgnoredContacts(Array.isArray(data.ignoredContacts) ? data.ignoredContacts : []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingIgnoredContacts(false);
    }
  }, [ignoredContactsEndpoint]);

  useEffect(() => {
    if (ignoredContactsModalOpen) {
      fetchIgnoredContacts();
    }
  }, [fetchIgnoredContacts, ignoredContactsModalOpen]);

  const handleOpenIgnoredContactsModal = tool => {
    setChatbotMenuAnchor(null);
    setAiMenuAnchor(null);
    setIgnoredContactsTool(tool);
    setIgnoredContactsModalOpen(true);
  };

  const requestAddIgnoredContact = () => {
    if (!currentContactId || savingIgnoredContact) return;

    setPendingIgnoredAction({
      action: "add",
      contactId: currentContactId,
      contactName: currentContactName
    });
  };

  const requestRemoveIgnoredContact = (contactId, contactName) => {
    if (!contactId || savingIgnoredContact) return;

    setPendingIgnoredAction({
      action: "remove",
      contactId,
      contactName: contactName || `Contacto #${contactId}`
    });
  };

  const handleAddIgnoredContact = async () => {
    if (!currentContactId || savingIgnoredContact) return;

    setSavingIgnoredContact(true);

    try {
      await api.post(ignoredContactsEndpoint, { contactId: currentContactId });
      toast.success(`Contacto agregado a ignorados de ${ignoredContactsToolLabel}.`);
      if (ignoredContactsTool === "ai") {
        setAiIgnored(true);
      } else {
        setChatbotIgnored(true);
      }
      await fetchIgnoredContacts();
    } catch (err) {
      toastError(err);
    } finally {
      setSavingIgnoredContact(false);
    }
  };

  const handleRemoveIgnoredContact = async contactId => {
    if (!contactId || savingIgnoredContact) return;

    setSavingIgnoredContact(true);

    try {
      await api.delete(`${ignoredContactsEndpoint}/${contactId}`);
      toast.success(`Contacto quitado de ignorados de ${ignoredContactsToolLabel}.`);
      if (String(contactId) === String(currentContactId)) {
        if (ignoredContactsTool === "ai") {
          setAiIgnored(false);
        } else {
          setChatbotIgnored(false);
        }
      }
      await fetchIgnoredContacts();
    } catch (err) {
      toastError(err);
    } finally {
      setSavingIgnoredContact(false);
    }
  };

  const handleConfirmIgnoredAction = async () => {
    if (!pendingIgnoredAction) return;

    if (pendingIgnoredAction.action === "add") {
      await handleAddIgnoredContact();
    } else {
      await handleRemoveIgnoredContact(pendingIgnoredAction.contactId);
    }

    setPendingIgnoredAction(null);
  };

  if (!ticket?.id || ticket.status !== "open") return null;

  return (
    <>
      <div className={classes.container}>
        {visibleTools.chatbot && (
        <Tooltip title={chatbotIgnored ? "Chatbot ignorado" : chatbotActive ? "Chatbot activo" : "Chatbot inactivo"}>
          <button
            type="button"
            className={`${classes.button} ${chatbotIgnored ? classes.ignored : chatbotActive ? classes.active : classes.inactive}`}
            onClick={event => setChatbotMenuAnchor(event.currentTarget)}
            aria-pressed={chatbotActive}
          >
            🤖
          </button>
        </Tooltip>
        )}
        {visibleTools.ai && (
        <Tooltip title={aiIgnored ? "IA ignorada" : aiActive ? "IA activa" : "IA inactiva"}>
          <button
            type="button"
            className={`${classes.button} ${aiIgnored ? classes.ignored : aiActive ? classes.active : classes.inactive}`}
            onClick={event => setAiMenuAnchor(event.currentTarget)}
            aria-pressed={aiActive}
          >
            🧠
          </button>
        </Tooltip>
        )}
        {visibleTools.reminders && (
        <Tooltip title="Recordatorios">
          <button
            type="button"
            className={`${classes.button} ${toolStates.reminders ? classes.active : classes.inactive}`}
            onClick={() => setTicketRemindersModalOpen(true)}
          >
            ⏰
          </button>
        </Tooltip>
        )}
        {visibleTools.agenda && (
        <Tooltip title="Agendar">
          <button
            type="button"
            className={`${classes.button} ${toolStates.agenda ? classes.active : classes.inactive}`}
            onClick={() => setTicketAgendaModalOpen(true)}
          >
            📅
          </button>
        </Tooltip>
        )}
        {visibleTools.orders && (
        <Tooltip title="Pedidos">
          <button
            type="button"
            className={`${classes.button} ${toolStates.orders ? classes.active : classes.inactive}`}
            onClick={() => setTicketOrdersModalOpen(true)}
          >
            🛒
          </button>
        </Tooltip>
        )}
        {visibleTools.processes && (
        <Tooltip title="Procesos IA">
          <button
            type="button"
            className={`${classes.button} ${toolStates.processes ? classes.active : classes.inactive}`}
            onClick={() => setTicketProcessesModalOpen(true)}
          >
            📃
          </button>
        </Tooltip>
        )}
        {visibleTools.streaming && (
        <Tooltip title="Streaming">
          <button
            type="button"
            className={`${classes.button} ${toolStates.streaming ? classes.active : classes.inactive}`}
            onClick={() => setTicketStreamingModalOpen(true)}
          >
            📺
          </button>
        </Tooltip>
        )}
        <Tooltip title={toolsOpen ? "Ocultar tools" : "Mostrar tools"}>
          <button
            type="button"
            className={`${classes.button} ${toolsOpen ? classes.active : classes.inactive}`}
            onClick={onToggleTools}
            aria-pressed={toolsOpen}
          >
            🛠️
          </button>
        </Tooltip>
      </div>
      <Menu
        anchorEl={chatbotMenuAnchor}
        keepMounted
        open={Boolean(chatbotMenuAnchor)}
        onClose={() => setChatbotMenuAnchor(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "center", horizontal: "right" }}
        transformOrigin={{ vertical: "center", horizontal: "left" }}
      >
        <MenuItem
          onClick={() => {
            setChatbotMenuAnchor(null);
            runChatbotAction(chatbotActive ? "deactivate" : "activate");
          }}
        >
          {chatbotActive ? "Apagar" : "Activar"}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setChatbotMenuAnchor(null);
            runChatbotAction(chatbotActive ? "deactivateReset" : "activateReset");
          }}
        >
          {chatbotActive ? "Apagar y Resetear" : "Activar y Resetear"}
        </MenuItem>
        <MenuItem onClick={() => handleOpenIgnoredContactsModal("chatbot")}>
          Lista de Ignorados
        </MenuItem>
      </Menu>
      <Menu
        anchorEl={aiMenuAnchor}
        keepMounted
        open={Boolean(aiMenuAnchor)}
        onClose={() => setAiMenuAnchor(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "center", horizontal: "right" }}
        transformOrigin={{ vertical: "center", horizontal: "left" }}
      >
        <MenuItem
          onClick={() => {
            setAiMenuAnchor(null);
            runAiAction(aiActive ? "deactivate" : "activate");
          }}
        >
          {aiActive ? "Apagar" : "Activar"}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAiMenuAnchor(null);
            runAiAction(aiActive ? "deactivateReset" : "activateReset");
          }}
        >
          {aiActive ? "Apagar y Resetear" : "Activar y Resetear"}
        </MenuItem>
        <MenuItem onClick={() => handleOpenIgnoredContactsModal("ai")}>
          Lista de Ignorados
        </MenuItem>
      </Menu>
      <TicketRemindersModal
        modalOpen={ticketRemindersModalOpen}
        onClose={() => {
          setTicketRemindersModalOpen(false);
          fetchStatus();
        }}
        ticket={ticket}
      />
      <TicketAgendaModal
        modalOpen={ticketAgendaModalOpen}
        onClose={() => {
          setTicketAgendaModalOpen(false);
          fetchStatus();
        }}
        ticket={ticket}
      />
      <TicketOrdersModal
        modalOpen={ticketOrdersModalOpen}
        onClose={() => {
          setTicketOrdersModalOpen(false);
          fetchStatus();
        }}
        ticket={ticket}
      />
      <TicketProcessesModal
        modalOpen={ticketProcessesModalOpen}
        onClose={() => {
          setTicketProcessesModalOpen(false);
          fetchStatus();
        }}
        ticket={ticket}
      />
      <TicketStreamingModal
        modalOpen={ticketStreamingModalOpen}
        onClose={() => {
          setTicketStreamingModalOpen(false);
          fetchStatus();
        }}
        ticket={ticket}
      />
      <Dialog
        open={ignoredContactsModalOpen}
        onClose={() => setIgnoredContactsModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Lista de Ignorados - {ignoredContactsToolLabel}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" gutterBottom>
            Contacto actual
          </Typography>
          <div className={classes.ignoredContactRow}>
            <div className={classes.ignoredContactInfo}>
              <Avatar src={currentContactAvatar}>
                {currentContactName.charAt(0).toUpperCase()}
              </Avatar>
              <div className={classes.ignoredContactText}>
                <Typography className={classes.ignoredContactName} noWrap>
                  {currentContactName}
                </Typography>
                {currentContactNumber && (
                  <Typography variant="body2" color="textSecondary" noWrap>
                    {currentContactNumber}
                  </Typography>
                )}
              </div>
            </div>
            <Button
              color={currentContactIgnored ? "secondary" : "primary"}
              variant="contained"
              disabled={!currentContactId || savingIgnoredContact}
              onClick={
                currentContactIgnored
                  ? () => requestRemoveIgnoredContact(currentContactId, currentContactName)
                  : requestAddIgnoredContact
              }
            >
              {currentContactIgnored ? "Quitar" : "Agregar"}
            </Button>
          </div>
          <Typography variant="subtitle2">
            Contactos ignorados
          </Typography>
          {loadingIgnoredContacts ? (
            <CircularProgress size={24} />
          ) : (
            <List dense className={classes.ignoredList}>
              {!ignoredContacts.length && (
                <Typography variant="body2" color="textSecondary">
                  No hay contactos ignorados.
                </Typography>
              )}
              {ignoredContacts.map(item => {
                const contact = item.contact || {};
                const contactId = item.contactId || contact.id;
                const contactName = contact.name || contact.number || `Contacto #${contactId}`;
                const contactNumber = contact.number || contact.chatId || "";
                const contactAvatar = contact.profilePicUrl || "";

                return (
                  <React.Fragment key={item.id || contactId}>
                    <ListItem className={classes.ignoredListItem}>
                      <Avatar src={contactAvatar} className={classes.ignoredListAvatar}>
                        {contactName.charAt(0).toUpperCase()}
                      </Avatar>
                      <ListItemText
                        primary={contactName}
                        secondary={contactNumber}
                      />
                      {String(contactId) !== String(currentContactId) && (
                        <Button
                          color="secondary"
                          variant="outlined"
                          disabled={savingIgnoredContact}
                          onClick={() => requestRemoveIgnoredContact(contactId, contactName)}
                        >
                          Quitar
                        </Button>
                      )}
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIgnoredContactsModalOpen(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(pendingIgnoredAction)}
        onClose={() => setPendingIgnoredAction(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {pendingIgnoredAction?.action === "add" ? "Agregar a ignorados" : "Quitar de ignorados"}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">
            {pendingIgnoredAction?.action === "add"
              ? `¿Quieres agregar a ${pendingIgnoredAction?.contactName || "este contacto"} a ignorados de ${ignoredContactsToolLabel}?`
              : `¿Quieres quitar a ${pendingIgnoredAction?.contactName || "este contacto"} de ignorados de ${ignoredContactsToolLabel}?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPendingIgnoredAction(null)}
            disabled={savingIgnoredContact}
          >
            Cancelar
          </Button>
          <Button
            color={pendingIgnoredAction?.action === "add" ? "primary" : "secondary"}
            variant="contained"
            onClick={handleConfirmIgnoredAction}
            disabled={savingIgnoredContact}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TicketFloatingControls;
